/**
 * Start Stripe Checkout for a plan intent.
 *
 * Mirrors the request CoachUpgradeHub already sends (same endpoint, same CSRF
 * header, same trialDays rule) so there is one checkout contract in the app,
 * not two that drift. The difference is the return URLs: a coach arriving here
 * came straight from signup, so cancelling should land them in the app rather
 * than on the Settings billing tab they have never seen.
 */

import { createCoachCheckout } from '@/data/billingApi';
import { trialDaysForTier, type PlanIntent } from './planIntent';

export interface StartCheckoutResult {
  ok: boolean;
  /** Present on success — caller navigates to it. */
  url?: string;
  message?: string;
}

export async function startCheckout(
  intent: PlanIntent,
  opts?: { successUrl?: string; cancelUrl?: string },
): Promise<StartCheckoutResult> {
  try {
    const trialDays = trialDaysForTier(intent.tier);
    const origin = window.location.origin;

    const res = await createCoachCheckout({
      tier: intent.tier,
      interval: intent.interval,
      ...(trialDays ? { trialDays } : {}),
      successUrl: opts?.successUrl ?? `${origin}/dashboard?billing=success`,
      cancelUrl: opts?.cancelUrl ?? `${origin}/dashboard?billing=canceled`,
    });

    if (res.ok && res.url) return { ok: true, url: res.url };
    return { ok: false, message: res.message || 'Could not start checkout' };
  } catch {
    return { ok: false, message: 'Could not start checkout' };
  }
}
